<template>
    <PageHeader :title="$t('setup_selector.title')" backRoute="home" />

    <div class="px-4 py-2">
        <div class="mb-2 flex items-center justify-between">
            <span class="text-sm font-bold text-white">{{ $t('setup_selector.step') }}</span>
            <span class="text-sm font-bold text-white"
                >{{ stepInfo.current }}/{{ stepInfo.total ?? 4 }}</span
            >
        </div>
        <div
            class="bg-stroke-1 h-1 w-full"
            style="clip-path: polygon(4px 0%, 100% 0%, calc(100% - 5px) 100%, 0% 100%)"
        >
            <div
                class="bg-progress-gradient transition-300 h-1 rounded-full"
                :style="{
                    'clip-path': 'polygon(4px 0%, 100% 0%, calc(100% - 5px) 100%, 0% 100%)',
                    width: `${(stepInfo.current / (stepInfo.total ?? 4)) * 100}%`,
                }"
            ></div>
        </div>
    </div>

    <div class="flex-1 space-y-4 p-4">
        <p class="text-lg font-extrabold text-white uppercase">{{ $t('setup_selector.heading') }}</p>
        <SetupCard
            v-for="setup in dartSetups"
            :key="setup.id"
            :setup="setup"
            :selected="setup.selected"
            :setup-stats="setupStats"
            :selected-game-type="matchTypeKey"
            @selected="selectSetup(setup)"
        />
    </div>

    <div class="mb-4 flex space-x-3 px-4">
        <DiagonalCutButton @click="goBack" variant="secondary" cut-position="right-top">
            <svg class="size-3.5" viewBox="0 0 9 14" xmlns="http://www.w3.org/2000/svg">
                <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M3.41436 7.00008L8.70726 1.70718L7.29304 0.292969L0.585938 7.00008L7.29304 13.7072L8.70726 12.293L3.41436 7.00008Z"
                    fill="currentColor"
                />
            </svg>
            <span class="mt-0.5">{{ $t('general.previous') }}</span>
        </DiagonalCutButton>
        <DiagonalCutButton
            @click="nextStep"
            :disabled="!selectedSetup"
            variant="primary"
            cut-position="left-top"
        >
            <span class="mt-0.5">{{ $t('general.next') }}</span>
            <svg class="size-3.5" viewBox="0 0 9 14" xmlns="http://www.w3.org/2000/svg">
                <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M5.58563 6.99992L0.292742 12.2928L1.70696 13.707L8.41406 6.99992L1.70695 0.292818L0.292741 1.70703L5.58563 6.99992Z"
                    fill="currentColor"
                />
            </svg>
        </DiagonalCutButton>
    </div>
</template>

<script>
import { mapStores } from 'pinia'
import { useMatchStore } from '@/stores/match.js'
import { useAuthStore } from '@/stores/auth.js'
import PageHeader from '@/components/PageHeader.vue'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import SetupCard from '@/components/ui/SetupCard.vue'

export default {
    name: 'SetupSelectorView',
    components: {
        DiagonalCutButton,
        PageHeader,
        SetupCard,
    },
    computed: {
        ...mapStores(useMatchStore, useAuthStore),

        stepInfo() {
            return this.matchStore.getStepInfo()
        },
        dartSetups() {
            const setups = this.authStore.dart_setups || []
            return [...setups].sort((a, b) => {
                const gamesA = this.setupStats[a.id]?.[this.matchTypeKey]?.games_played || 0
                const gamesB = this.setupStats[b.id]?.[this.matchTypeKey]?.games_played || 0
                return gamesB - gamesA
            })
        },
        selectedSetup() {
            return this.dartSetups.find((setup) => setup.selected)
        },
        setupStats() {
            const stats = this.authStore.statistics?.setups || {}
            console.log('setup stats:', stats)
            return stats
        },
        matchTypeKey() {
            const matchtype = this.authStore.matchtypes?.find(
                (m) => m.id === this.matchStore.setup.matchtype_id,
            )
            if (!matchtype) return ''
            return 'matchtype_' + matchtype.name.toLowerCase().replace(/\s+/g, '_')
        },
    },
    async mounted() {
        const matchId = this.$route.params.matchId
        if (
            matchId &&
            this.matchStore.setup.id !== matchId &&
            this.matchStore.match.id !== matchId
        ) {
            try {
                await this.matchStore.restoreMatchFromAPI(matchId)
            } catch (error) {
                console.error('Failed to restore match:', error)
                this.$router.replace({ name: 'games' })
                return
            }
        }

        this.matchStore.setCurrentStep(3)

        this.dartSetups.forEach((setup) => {
            setup.selected = false
        })
    },
    methods: {
        goBack() {
            const matchId = this.$route.params.matchId
            if (matchId) {
                this.$router.push({
                    name: 'match-setup',
                    params: { matchId },
                })
            } else {
                this.$router.push({ name: 'home' })
            }
        },
        selectSetup(selectedSetup) {
            console.log('selected setup:', selectedSetup)

            this.dartSetups.forEach((setup) => {
                setup.selected = setup.id === selectedSetup.id
            })

            this.matchStore.setup.dart_setup_id = selectedSetup.id
        },
        async nextStep() {
            try {
                await this.matchStore.updateMatch()
                this.$router.push({
                    name: 'match-starter-selector',
                    params: { matchId: this.$route.params.matchId },
                })
            } catch (error) {
                console.error('Failed to create match:', error)
                this.$toast(
                    this.$t('general.error'),
                    5000,
                    this.$t('match_settings.error_create'),
                )
            }
        },
    },
}
</script>
